import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { MOCK_CHANGEOVER_SUMMARY, MOCK_JOB_HISTORY } from '../mock/mockData';
import CPMockBadge from './CPMockBadge';
import styles from './cpCOSummary.module.scss';

// Coated / Uncoated R2 minutes from the Excel
const COATED_UNCOATED: Record<string, { coated: number; uncoated: number }> = {
  'L1_HIGH': { coated: 60,  uncoated: 65  },
  'L1_LOW':  { coated: 60,  uncoated: 60  },
  'L1_OVER': { coated: 35,  uncoated: 35  },
  'L2_HIGH': { coated: 120, uncoated: 145 },
  'L2_LOW':  { coated: 75,  uncoated: 80  },
  'L2_OVER': { coated: 0,   uncoated: 0   },
  'L3_HIGH': { coated: 155, uncoated: 175 },
  'L3_LOW':  { coated: 105, uncoated: 105 },
  'L3_OVER': { coated: 60,  uncoated: 60  },
};

const CO_KEYS = [
  { level: 'L1', cat: 'HIGH' }, { level: 'L1', cat: 'LOW' }, { level: 'L1', cat: 'OVER' },
  { level: 'L2', cat: 'HIGH' }, { level: 'L2', cat: 'LOW' }, { level: 'L2', cat: 'OVER' },
  { level: 'L3', cat: 'HIGH' }, { level: 'L3', cat: 'LOW' }, { level: 'L3', cat: 'OVER' },
];

const MAX_HISTORY_MONTHS = 6; // MOCK_JOB_HISTORY has 6 months

function fmtJobMonth(yr: number, mth: number): string {
  return new Date(yr, mth - 1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
}

const CPCOSummary = observer(() => {
  const store = useCapacityStore();

  if (!store.dashboardData && !store.isDashboardLoading) {
    return (
      <div className={styles.noData}>
        <i className="pi pi-sync" />
        <p>No calculation data yet. Set your planning parameters and click <strong>Generate Dashboard</strong>.</p>
      </div>
    );
  }

  const lookback = Math.min(Math.round(store.planningParams?.lookback_months ?? 6), MAX_HISTORY_MONTHS);
  const scale = lookback / MAX_HISTORY_MONTHS;

  // ---- Section 1: Rolling jobs ----
  // Build the N months immediately before the planning start month
  const [startY, startM] = (store.startYearMonth || '2026-04').split('-').map(Number);
  const windowMonths: Array<{ yr: number; mth: number }> = [];
  for (let i = lookback; i >= 1; i--) {
    let m = startM - i;
    let y = startY;
    while (m <= 0) { m += 12; y--; }
    windowMonths.push({ yr: y, mth: m });
  }
  const rollingJobs = windowMonths.map(({ yr, mth }) =>
    MOCK_JOB_HISTORY.find(r => r.yr === yr && r.mth === mth) ?? null
  );
  const validJobs = rollingJobs.filter((r): r is NonNullable<typeof r> => r !== null);
  const avgJobs = validJobs.length > 0
    ? validJobs.reduce((s, r) => s + r.jobs, 0) / validJobs.length
    : 0;

  // ---- Section 2: Combined changeover profile ----
  const profileRows = CO_KEYS.map(({ level, cat }) => {
    const sumRow = MOCK_CHANGEOVER_SUMMARY.find(r => r.clean_level === level && r.room_category === cat);
    const key = `${level}_${cat}`;
    const count = sumRow ? Math.round(sumRow.changeover_count * scale) : 0;
    const totalCount = Math.round(
      MOCK_CHANGEOVER_SUMMARY.reduce((s, r) => s + r.changeover_count, 0) * scale
    );
    const freq = totalCount > 0 ? count / totalCount : 0;
    const avgMins = sumRow ? sumRow.avg_r2_minutes : 0;
    const monthlyHrs = sumRow ? (sumRow.total_r2_minutes * scale) / 60 : 0;
    const cu = COATED_UNCOATED[key] ?? { coated: 0, uncoated: 0 };
    return { key, label: `${level} ${cat}`, count, freq, avgMins, monthlyHrs, coated: cu.coated, uncoated: cu.uncoated };
  });
  const totalCount = profileRows.reduce((s, r) => s + r.count, 0);
  const totalHrs = profileRows.reduce((s, r) => s + r.monthlyHrs, 0);

  return (
    <div className={styles.wrapper}>
      <div className={styles.pageHeader}>
        <i className="pi pi-sync" />
        <h2>CO Summary</h2>
        <CPMockBadge />
      </div>

      {/* ---- Section 1: Rolling Jobs ---- */}
      <div className={styles.sectionBlock}>
        <div className={styles.sectionHeader}>
          <i className="pi pi-calendar" />
          <h3>{lookback}-Month Rolling Jobs</h3>
        </div>
        <table className={styles.jobsTable}>
          <thead>
            <tr>
              <th>Month</th>
              <th className={styles.numCell}>Jobs</th>
            </tr>
          </thead>
          <tbody>
            {windowMonths.map(({ yr, mth }, i) => {
              const r = rollingJobs[i];
              return (
                <tr key={`${yr}-${mth}`}>
                  <td>{fmtJobMonth(yr, mth)}</td>
                  <td className={styles.numCell}>{r ? r.jobs.toLocaleString() : '—'}</td>
                </tr>
              );
            })}
            <tr className={styles.rowAvg}>
              <td>Avg Jobs/Mth</td>
              <td className={styles.numCell}>{avgJobs.toFixed(1)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ---- Section 2: Combined Changeover Profile ---- */}
      <div className={styles.sectionBlock}>
        <div className={styles.sectionHeader}>
          <i className="pi pi-chart-bar" />
          <h3>Changeover Profile</h3>
          <span className={styles.sectionNote}>Based on {lookback}-month lookback</span>
        </div>
        <div className={styles.scrollOuter}>
          <table className={styles.profileTable}>
            <thead>
              <tr>
                <th className={styles.labelColHeader}>C/O</th>
                <th className={styles.numCell}>Monthly Hrs</th>
                <th className={styles.numCell}>Count</th>
                <th className={styles.numCell}>Frequency</th>
                <th className={styles.numCell}>R2 Mins</th>
                <th className={styles.numCell}>Coated</th>
                <th className={styles.numCell}>Uncoated</th>
              </tr>
            </thead>
            <tbody>
              {profileRows.map(row => (
                <tr key={row.key}>
                  <td className={styles.labelCol}>{row.label} Hrs</td>
                  <td className={styles.numCell}>{row.monthlyHrs.toFixed(1)}</td>
                  <td className={styles.numCell}>{row.count}</td>
                  <td className={styles.numCell}>
                    {totalCount > 0 ? `${Math.round(row.freq * 100)}%` : '0%'}
                  </td>
                  <td className={styles.numCell}>{row.avgMins.toFixed(1)}</td>
                  <td className={styles.numCell}>{row.coated}</td>
                  <td className={styles.numCell}>{row.uncoated}</td>
                </tr>
              ))}
              <tr className={styles.rowTotal}>
                <td className={styles.labelCol}>Total</td>
                <td className={styles.numCell}>{totalHrs.toFixed(1)}</td>
                <td className={styles.numCell}>{totalCount}</td>
                <td className={styles.numCell}>100%</td>
                <td className={styles.numCell}>—</td>
                <td className={styles.numCell}>—</td>
                <td className={styles.numCell}>—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default CPCOSummary;
